// @greenlight-spec: 16-api-conventions.md
// Shared error-message helpers for mutation call sites. Understand the
// ts-rest error shape `{ status, body: { message } }` and plain Error objects.

export function mutationErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'body' in err) {
    const body = (err as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'message' in body) {
      return String((body as { message?: unknown }).message);
    }
  }
  return err instanceof Error ? err.message : 'Request failed';
}

/** Pulls the canonical error `message` out of a non-200 response body. */
export function errorBodyMessage(body: unknown, fallback: string): string {
  if (
    body !== null &&
    typeof body === 'object' &&
    'message' in body &&
    typeof (body as { message: unknown }).message === 'string'
  ) {
    return (body as { message: string }).message;
  }
  return fallback;
}

/** Pulls the canonical error `code` out of a non-200 response body, or
 * `undefined` when absent — lets a caller branch on a specific code (e.g. to
 * show a distinct recovery action) instead of matching on message text. */
export function errorBodyCode(body: unknown): string | undefined {
  if (body !== null && typeof body === 'object' && 'code' in body) {
    const code = (body as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** The ts-rest client throws `{ status, body }` for non-2xx responses — pulls
 * the server's message out of the thrown value, with a caller-supplied
 * fallback for a real network failure (no `body` on the thrown value) instead
 * of a raw `Error#message` like "Failed to fetch". */
export function thrownMessage(err: unknown, fallback: string): string {
  if (err !== null && typeof err === 'object' && 'body' in err) {
    return errorBodyMessage((err as { body: unknown }).body, fallback);
  }
  return fallback;
}

/** Pulls the canonical error `code` out of a thrown ts-rest non-2xx response,
 * or `undefined` for a real network failure — lets a caller branch on a
 * specific code (e.g. to show a distinct recovery action) instead of matching
 * on message text. */
export function thrownCode(err: unknown): string | undefined {
  if (err === null || typeof err !== 'object' || !('body' in err)) return undefined;
  return errorBodyCode((err as { body: unknown }).body);
}
